import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';

export interface Group {
  id: string;
  name: string;
  description?: string;
  groupFields?: any;
  parentId?: string | null;
  children?: Group[];
}

interface GroupCache {
  [key: string]: Group[];
}

// Module-level cache -- survives component unmounts, persists for the session
let groupsCache: GroupCache = {};

export function clearGroupsCache() {
  groupsCache = {};
  console.log('Groups cache cleared');
}

export const useGroupChildren = (parentId: string | null) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { makeAuthenticatedCall } = useAuthenticatedApi();

  const fetchGroups = useCallback(async () => {
    const cacheKey = parentId || 'root';
    
    // Check cache first
    if (groupsCache[cacheKey]) {
      console.log('Using cached groups for:', cacheKey);
      setGroups(groupsCache[cacheKey]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await makeAuthenticatedCall('get-groups', {
        parentId: parentId || ''
      });

      if (error) {
        throw new Error(error);
      }

      const groupsData = (data as Group[]) || [];
      
      // Store in cache
      groupsCache[cacheKey] = groupsData;
      
      setGroups(groupsData);
      console.log('Groups fetched successfully:', groupsData.length, 'groups for parent:', cacheKey);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch groups';
      setError(errorMessage);
      console.error('Error fetching groups:', err);
    } finally {
      setIsLoading(false);
    }
  }, [parentId, makeAuthenticatedCall]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    isLoading,
    error,
    refetch: fetchGroups,
  };
};
